import { useState, useEffect } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { OrganizationWithRelations } from "@/types/organization.types";
import { useOrganizations } from "@/hooks/useOrganizations";

interface MergeOrganizationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  organization: OrganizationWithRelations;
  onMerge: (sourceId: string, targetId: string) => Promise<void>;
}

export function MergeOrganizationDialog({
  isOpen,
  onClose,
  organization,
  onMerge,
}: MergeOrganizationDialogProps) {
  const [isMerging, setIsMerging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [targetId, setTargetId] = useState<string>("");
  const { organizations, isLoading } = useOrganizations();

  // Reset the form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setTargetId("");
      setError(null);
    }
  }, [isOpen]);

  const handleMerge = async () => {
    if (!targetId) {
      setError("Please select a target organization");
      return;
    }

    try {
      setIsMerging(true);
      setError(null);
      await onMerge(organization.id, targetId);
      onClose();
    } catch (err: any) {
      setError(err.message || "An error occurred while merging the organizations");
    } finally {
      setIsMerging(false);
    }
  };

  // Filter out the current organization from the list
  const availableOrganizations = organizations.filter(
    (org) => org.id !== organization.id
  );

  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Merge {organization.name}</AlertDialogTitle>
          <AlertDialogDescription>
            Select the organization to merge this one into. All data from{" "}
            <strong>{organization.name}</strong> will be transferred to the selected
            organization, and then <strong>{organization.name}</strong> will be
            deleted.
            <div className="mt-4">
              <p className="mb-2">Target Organization:</p>
              <Select
                disabled={isMerging || isLoading}
                value={targetId}
                onValueChange={setTargetId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select an organization" />
                </SelectTrigger>
                <SelectContent>
                  {isLoading ? (
                    <SelectItem value="loading" disabled>
                      Loading organizations...
                    </SelectItem>
                  ) : availableOrganizations.length === 0 ? (
                    <SelectItem value="none" disabled>
                      No other organizations available
                    </SelectItem>
                  ) : (
                    availableOrganizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            {error && (
              <div className="mt-2 rounded-md bg-destructive/10 p-3 text-destructive">
                {error}
              </div>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isMerging}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleMerge}
            disabled={isMerging || !targetId}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {isMerging ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Merging...
              </>
            ) : (
              "Merge"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}